import { AnalyticsEventNameType, DataSource, DataSourceType } from "@coeff/api";
import { COLORS, WARNING_MESSAGES, WARNING_TITLES } from "@coeff/utils";
import { Layout } from "antd";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Graphics } from "../../../common/Graphics";
import { ConnectPlugIcon } from "../../../common/Icons";
import { ImportTypeIcon } from "../../../common/ImportTypeIcon";
import { Loader } from "../../../common/Loader";
import { WarningMessage } from "../../../common/WarningMessage";
import { useApiClient, useEventManagerContext, useMessage } from "../../../globals/contexts";
import { VCRMessageEnvelope } from "../../../types";
import {
  BaseAuthorizeLocationState,
  QueryCacheKey,
  useAuthContext,
  WithPostDataSourceCreationProps,
} from "../../context/AuthContext";
import { getAuthorizeDescription } from "../../utils";

const { Content } = Layout;

const StyledContent = styled(Content)<{ isModal?: boolean }>`
  && {
    background-color: ${COLORS.white};

    ${props =>
      props.isModal
        ? `
          `
        : `
          height: 100%;
          min-height: calc(100vh - 132px);
          margin-top: 64px;
      `}

    padding: 25px 0px 15px;

    .graphicsLogo {
      margin: 0px auto 20px;

      .ant-avatar-lg {
        width: 60px;
        height: 60px;
        line-height: 60px;
        position: relative;
        top: 5px;

        svg {
          width: 60px;
          height: 60px;
        }
      }
    }

    h3.title {
      margin: 0px auto;
      font-weight: bold;
    }

    .label {
      color: ${COLORS.black85};
      font-weight: bold;
      margin-top: 10px;
      text-align: left;
    }
  }
`;

const StyledLogo = styled(ImportTypeIcon)`
  display: block;
  width: 60px;
  height: 60px;
  margin: auto;
`;

export interface AuthorizeAmplitudeLocationState extends BaseAuthorizeLocationState {
  dataSource?: DataSource;
  dataImport?: any;
  redirect?: string;
  redirectOnBack?: string;
}

type Props = WithPostDataSourceCreationProps & {
  isModal?: boolean;
  dataSourceId: string | undefined;
  fromConnectedSource: boolean | undefined;
  fromOnboarding: boolean | undefined;
  onCancel: () => void;
  onDataSourceUpserted: (dataSource: DataSource) => void;
  event_props?: { email?: string; domain?: string };
  // Base URL of the webapp that hosts the web scraper auth page. Provided by the
  // consuming app (addon passes WEBAPP_BASE_URL; webapp uses same-origin "").
  webappBaseUrl?: string;
};

const AuthorizeAmplitudeContentBase: React.FC<Props> = ({
  isModal,
  dataSourceId,
  fromConnectedSource,
  fromOnboarding,
  event_props = {},
  initPostDataSourceCreation,
  onDataSourceUpserted,
  onCancel,
  webappBaseUrl = "",
}) => {
  const message = useMessage();
  const apiClient = useApiClient();
  const { track } = useEventManagerContext();
  const { sentryCapture } = useAuthContext();

  const [oauthInProgress, setOauthInProgress] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState<boolean>(false);
  const [activePollingInterval, setActivePollingInterval] = useState<NodeJS.Timeout | undefined>();
  const [authWindowHandler, setAuthWindowHandler] = useState<Window | null>(null);
  const [authWindowMessageListener, setAuthWindowMessageListener] = useState<EventListener | null>(
    null
  );

  const setUpAuthSessionListeners = (windowHandler: Window | null) => {
    if (!windowHandler) {
      return;
    }

    track("web_scraper_auth_page_opened", { conn_data_source: "amplitude" });

    const pollingIntervalId = setInterval(() => {
      if (windowHandler?.closed) {
        track("web_scraper_auth_page_closed", { conn_data_source: "amplitude" });
        clearInterval(pollingIntervalId);
        onCancel();
      }
    }, 500);

    setActivePollingInterval(pollingIntervalId);

    const authWindowListener = (event: Event) => {
      if (!event) {
        return;
      }

      if (!(event instanceof MessageEvent)) {
        return;
      }

      const eventData = event.data as VCRMessageEnvelope;

      if (!eventData) {
        return;
      }

      const messageType = eventData.messageType;

      if (messageType === "request_auth_props") {
        if (!windowHandler) {
          return;
        }

        windowHandler.postMessage(
          {
            messageType: "auth_props",
            dataSourceType: "amplitude",
            authUrl: dataSourceId
              ? "https://app.amplitude.com/login"
              : "https://app.amplitude.com/",
          } as VCRMessageEnvelope,
          "*"
        );
      } else if (messageType === "auth_complete") {
        clearInterval(pollingIntervalId);

        if (eventData.success && eventData.dataSource) {
          track(dataSourceId ? "conn_edit_done" : "conn_create_done", {
            import_data_source: "amplitude",
            connection_data_source: "amplitude",
            data_source_id: eventData.dataSource?.data_source_id,
          });

          if (dataSourceId) {
            message.success("Connection saved");
            onDataSourceUpserted(eventData.dataSource);
          } else {
            initPostDataSourceCreation({
              dataSource: eventData.dataSource,
              onComplete: dataSource => onDataSourceUpserted(dataSource),
            });
          }
        } else {
          track(dataSourceId ? "conn_edit_failed" : "conn_create_failed", {
            import_data_source: "amplitude",
            data_source_id: dataSourceId,
          });

          onCancel();
        }
      }
    };

    window.addEventListener("message", authWindowListener);

    setAuthWindowMessageListener(authWindowListener);
  };

  const onAuthClick = () => {
    if (!dataSourceId) {
      track("conn_create_started", {
        import_data_source: "amplitude",
        connection_data_source: "amplitude",
        data_source_id: dataSourceId,
      });
    }

    track("import_oauth_started", {
      import_data_source: "amplitude",
      connection_data_source: "amplitude",
      data_source_id: dataSourceId,
    });

    setOauthInProgress(true);
    setPopupBlocked(false);

    try {
      const windowHandler = window.open(`${webappBaseUrl}/web_scraper/auth`);

      if (!windowHandler || windowHandler.closed || typeof windowHandler.closed === "undefined") {
        track(AnalyticsEventNameType.AuthorizationWindowPopupBlocked, {
          data_source: DataSourceType.Amplitude,
          data_source_id: dataSourceId,
        });
        setPopupBlocked(true);
        setOauthInProgress(false);
        return;
      }

      setAuthWindowHandler(windowHandler);
      setUpAuthSessionListeners(windowHandler);
    } catch (error: unknown) {
      message.error("Error initializing connection. Try again.");

      track("import_oauth_failed", {
        import_data_source: "amplitude",
        data_source_id: dataSourceId,
        reason: error instanceof Error ? error.message : String(error),
      });

      track(dataSourceId ? "conn_edit_failed" : "conn_create_failed", {
        import_data_source: "amplitude",
        data_source_id: dataSourceId,
        reason: error instanceof Error ? error.message : String(error),
      });

      setOauthInProgress(false);
    }
  };

  useEffect(() => {
    return () => {
      if (authWindowHandler) {
        authWindowHandler.postMessage(
          {
            messageType: "force_close_session",
            errorMessage: "Auth session has been terminated from the addon.",
          } as VCRMessageEnvelope,
          "*"
        );
      }

      if (authWindowMessageListener) {
        window.removeEventListener("message", authWindowMessageListener);
      }

      if (activePollingInterval !== undefined) {
        clearInterval(activePollingInterval);
      }

      apiClient.vcrTerminateAuthSession().catch(e => {
        //  It's possible that webapp already terminated the session, we will get 500s here
        console.error(e);
      });
    };
  }, []);

  useEffect(() => {
    if (!dataSourceId) {
      track("conn_create_viewed", {
        import_data_source: "amplitude",
        event_from: fromConnectedSource
          ? "add_connection"
          : fromOnboarding
          ? "app_onboarding"
          : "add_import",
        data_source_id: dataSourceId,
      });
    }
  }, [dataSourceId, fromConnectedSource, fromOnboarding, track]);

  return (
    <StyledContent isModal={isModal}>
      {popupBlocked && (
        <WarningMessage
          title={WARNING_TITLES.POPUP_BLOCKED}
          message={WARNING_MESSAGES.POPUP_BLOCKED}
        />
      )}

      <Graphics
        graphics={
          <div className="graphicsLogo">
            <ConnectPlugIcon />
            <StyledLogo type={"amplitude"} />
          </div>
        }
        title={dataSourceId ? `Reconnect to Amplitude` : `Let's Connect to Amplitude`}
        description={getAuthorizeDescription("amplitude", dataSourceId)}
        buttonText={
          oauthInProgress ? (
            <div>
              <Loader verticalAlign color="white" />
            </div>
          ) : (
            "Authorize"
          )
        }
        buttonProps={{
          disabled: oauthInProgress,
          onClick: () => onAuthClick(),
        }}
      />
    </StyledContent>
  );
};

export const AuthorizeAmplitudeContent = AuthorizeAmplitudeContentBase;
